import { useState, useEffect } from 'react';

/**
 * HoursOfOperationForm
 * Component for setting business hours for each day of the week
 * 
 * Props:
 * - hours: Object | null - Current hours (e.g., { monday: { open: '09:00', close: '17:00' }, ... })
 * - onHoursChange: (hours: Object) => void - Called when hours change
 */
const HoursOfOperationForm = ({ hours = null, onHoursChange }) => {
  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  // Initialize form state
  const defaultHours = days.reduce((acc, day) => {
    acc[day.key] = {
      open: '09:00',
      close: '17:00',
      closed: false,
    };
    return acc;
  }, {});

  const [formHours, setFormHours] = useState(hours || defaultHours);
  const [uniformHours, setUniformHours] = useState(false);
  const [uniformOpen, setUniformOpen] = useState('09:00');
  const [uniformClose, setUniformClose] = useState('17:00');

  /**
   * Update individual day's time
   */
  const updateDayTime = (dayKey, field, value) => {
    const updated = { ...formHours };
    updated[dayKey] = { ...updated[dayKey], [field]: value };
    setFormHours(updated);
    onHoursChange(updated);
  };

  /**
   * Toggle closed status for a day
   */
  const toggleDayClosed = (dayKey) => {
    const updated = { ...formHours };
    updated[dayKey] = {
      ...updated[dayKey],
      closed: !updated[dayKey].closed,
    };
    setFormHours(updated);
    onHoursChange(updated);
  };

  /**
   * Apply uniform hours to all days
   */
  const applyUniformHours = () => {
    const updated = days.reduce((acc, day) => {
      acc[day.key] = {
        open: uniformOpen,
        close: uniformClose,
        closed: false,
      };
      return acc;
    }, {});
    setFormHours(updated);
    onHoursChange(updated);
  };

  /**
   * Check if all days have same hours
   */
  const allSameHours = days.every(
    (day) =>
      formHours[day.key].open === formHours[days[0].key].open &&
      formHours[day.key].close === formHours[days[0].key].close &&
      formHours[day.key].closed === formHours[days[0].key].closed
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-[12px] font-semibold text-slate-700 mb-3">
          Hours of Operation (Optional)
        </label>

        {/* Uniform hours helper */}
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
          <div>
            <label className="block text-[11px] font-medium text-slate-700 mb-2">
              Apply same hours to all days
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="time"
                value={uniformOpen}
                onChange={(e) => setUniformOpen(e.target.value)}
                className="px-2 py-1.5 text-[12px] border border-slate-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-[11px] text-slate-500">to</span>
              <input
                type="time"
                value={uniformClose}
                onChange={(e) => setUniformClose(e.target.value)}
                className="px-2 py-1.5 text-[12px] border border-slate-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={applyUniformHours}
                className="px-3 py-1.5 text-[11px] font-medium bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Apply
              </button>
            </div>
            {allSameHours && (
              <p className="text-[10px] text-green-600 mt-1">✓ All days have uniform hours</p>
            )}
          </div>
        </div>

        {/* Days list */}
        <div className="space-y-2.5">
          {days.map((day) => {
            const dayHours = formHours[day.key];
            return (
              <div
                key={day.key}
                className={`flex items-center gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded-lg transition-colors ${
                  dayHours.closed ? 'opacity-60' : ''
                }`}
              >
                {/* Day label */}
                <div className="w-24 shrink-0">
                  <label className="text-[12px] font-semibold text-slate-700">
                    {day.label}
                  </label>
                </div>

                {/* Closed toggle */}
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    id={`closed-${day.key}`}
                    type="checkbox"
                    checked={dayHours.closed}
                    onChange={() => toggleDayClosed(day.key)}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 cursor-pointer"
                  />
                  <label
                    htmlFor={`closed-${day.key}`}
                    className="text-[11px] text-slate-600 cursor-pointer select-none"
                  >
                    Closed
                  </label>
                </div>

                {/* Time inputs */}
                {!dayHours.closed && (
                  <div className="flex gap-2 flex-1 items-center">
                    <input
                      type="time"
                      value={dayHours.open}
                      onChange={(e) => updateDayTime(day.key, 'open', e.target.value)}
                      className="flex-1 px-2 py-1.5 text-[12px] text-slate-700 border border-slate-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <span className="text-[11px] text-slate-500 shrink-0">to</span>
                    <input
                      type="time"
                      value={dayHours.close}
                      onChange={(e) => updateDayTime(day.key, 'close', e.target.value)}
                      className="flex-1 px-2 py-1.5 text-[12px] text-slate-700 border border-slate-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                )}

                {dayHours.closed && (
                  <div className="flex-1 text-center">
                    <span className="text-[11px] text-slate-500 font-medium">Closed</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-[11px] text-blue-900 font-medium mb-2">Hours Summary</p>
          <div className="space-y-1">
            {days.map((day) => {
              const dayHours = formHours[day.key];
              const summary = dayHours.closed
                ? 'Closed'
                : `${dayHours.open} - ${dayHours.close}`;
              return (
                <div key={day.key} className="flex justify-between text-[11px] text-blue-800">
                  <span className="font-medium">{day.label}:</span>
                  <span>{summary}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoursOfOperationForm;
